// covered-by: frontends/carrie/src/utils/__tests__/askPunttMentionNotifications.test.ts
import {
  NotificationType,
  type Notification,
} from "@mg/schemas/src/commons/user";

export type AskPunttMentionTarget = {
  chatId: string;
  projectId?: string;
};

const PROJECT_CHAT_PATH =
  /^\/ask-puntt\/projects\/([^/?#]+)\/chats\/([^/?#]+)(?:[/?#]|$)/;
const STANDALONE_CHAT_PATH = /^\/ask-puntt\/([^/?#]+)(?:[/?#]|$)/;
// why: `/ask-puntt/projects` and `/ask-puntt/chats` are list routes, not chat
// ids; the standalone regex would otherwise read them as a chat called
// "projects" and hand the caller a dead link.
const NON_CHAT_SEGMENTS = new Set(["projects", "chats"]);

/** Chat (and project, for a project chat) that a mention CTA link points into. */
export function mentionNotificationTarget(
  ctaLink: string,
): AskPunttMentionTarget | null {
  let pathname: string;
  try {
    const base =
      typeof window === "undefined"
        ? "https://app.puntt.ai"
        : window.location.origin;
    pathname = new URL(ctaLink, base).pathname;
  } catch {
    return null;
  }
  const askPunttIndex = pathname.indexOf("/ask-puntt/");
  if (askPunttIndex < 0) return null;
  const askPunttPath = pathname.slice(askPunttIndex);

  const projectChat = askPunttPath.match(PROJECT_CHAT_PATH);
  if (projectChat?.[1] && projectChat[2]) {
    return { projectId: projectChat[1], chatId: projectChat[2] };
  }

  const standalone = askPunttPath.match(STANDALONE_CHAT_PATH);
  const chatId = standalone?.[1];
  if (!chatId || NON_CHAT_SEGMENTS.has(chatId)) return null;
  return { chatId };
}

export function isMentionNotification(notification: Notification): boolean {
  return notification.type === NotificationType.PROJECT_MENTION;
}

/**
 * Where a mention notification leads. christo stores the chat id on
 * `subReference` (new rows); older rows only carry it inside `ctaLink`, so the
 * link is parsed as the fallback and still supplies the project id.
 */
export function resolveMentionNotificationTarget(
  notification: Notification,
): AskPunttMentionTarget | null {
  const fromLink = notification.ctaLink
    ? mentionNotificationTarget(notification.ctaLink)
    : null;
  const subReference = notification.subReference?.trim();
  if (subReference) {
    return fromLink?.projectId
      ? { chatId: subReference, projectId: fromLink.projectId }
      : { chatId: subReference };
  }
  return fromLink;
}

/** Stable identity for a mention row across profile polls. */
export function mentionNotificationKey(notification: Notification): string {
  return [
    String(notification.createdAt),
    String(notification.reference ?? ""),
    notification.subReference ?? "",
    notification.message ?? "",
    notification.ctaLink ?? "",
  ].join("");
}
